/**
 * ImagesSection 產品圖片 (V3-S2) — the detail-page seat for the mock-first
 * image gallery, mounted under 基本資料 directly after Descriptions
 * (TAB_SECTION_GROUPS pin in `__tests__/tabComposition.test.tsx`).
 *
 * Self-contained (PlanningSection precedent): renders the shared
 * `ProductImageGallery`, adds/edits URL rows through the SAME `ImageSlotModal`
 * the wizard uses, and writes through the EXISTING PATCH lane
 * (`useProductMutations.updateProduct` → store-first echo + cache
 * invalidation). Exactly ONE write path — demo persistence is a mock-layer
 * overlay invisible to this component (V3-S4).
 */

import { useState } from 'react';
import { App } from 'antd';
import { PictureOutlined, PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import ProductImageGallery from '@/components/common/ProductImageGallery';
import type { ProductGalleryImage } from '@/components/common/ProductImageGallery';
import { ListEmptyState } from '@/components/common/TableContainer';
import ImageSlotModal from '@/components/products/ImageSlotModal';
import type { ImageSlotRow } from '@/schemas/forms/productFormSchemas';
import { useProductMutations } from '@/hooks/useProducts';
import { MAX_PRODUCT_IMAGES } from '@/types/api/product';
import type { Product, ProductImageSlot } from '@/types/api/product';
import SectionHeader from './SectionHeader';

interface ImagesSectionProps {
  product: Product;
  onSaved: () => void;
}

/** Re-slot 1..N after any add/edit/remove — slot order is meaningful. */
const toSlots = (rows: ProductGalleryImage[]): ProductImageSlot[] =>
  rows.slice(0, MAX_PRODUCT_IMAGES).map((row, index) => ({
    slot: (index + 1) as ProductImageSlot['slot'],
    url: row.url,
    caption: row.caption?.trim() ? row.caption : undefined,
  }));

export const ImagesSection = ({ product, onSaved }: ImagesSectionProps) => {
  const { t } = useTranslation(['products', 'common']);
  const { message } = App.useApp();
  const { updateProduct } = useProductMutations(product.id);
  const [modal, setModal] = useState<{ open: boolean; row: ImageSlotRow | null }>({
    open: false,
    row: null,
  });

  const images = product.images ?? [];
  const editable = product.isActive;

  const writeImages = async (next: ProductImageSlot[]) => {
    try {
      await updateProduct({ images: next });
      message.success(t('products:images.saved'));
      onSaved();
    } catch {
      message.error(t('common:messages.updateFailed'));
    }
  };

  const handleSave = (row: ImageSlotRow) => {
    const exists = images.some((candidate) => candidate.slot === row.slot);
    const nextRows: ProductGalleryImage[] = exists
      ? images.map((candidate) => (candidate.slot === row.slot ? row : candidate))
      : [...images, row];
    void writeImages(toSlots(nextRows));
  };

  const handleRemove = (slot: number) => {
    void writeImages(toSlots(images.filter((candidate) => candidate.slot !== slot)));
  };

  return (
    <div>
      <SectionHeader
        title={t('products:fields.images')}
        subtitle={t('products:validation.imagesMax')}
      />
      {images.length === 0 ? (
        <ListEmptyState
          icon={<PictureOutlined />}
          title={t('products:images.empty')}
          actionLabel={editable ? t('products:images.addByUrl') : undefined}
          actionIcon={<PlusOutlined />}
          onAction={editable ? () => setModal({ open: true, row: null }) : undefined}
        />
      ) : (
        <ProductImageGallery
          images={images}
          editable={editable}
          maxImages={MAX_PRODUCT_IMAGES}
          onAdd={() => setModal({ open: true, row: null })}
          onEdit={(row) =>
            setModal({ open: true, row: { slot: row.slot, url: row.url, caption: row.caption } })
          }
          onRemove={handleRemove}
        />
      )}
      <ImageSlotModal
        open={modal.open}
        initial={modal.row}
        nextSlot={Math.min(images.length + 1, MAX_PRODUCT_IMAGES)}
        onClose={() => setModal({ open: false, row: null })}
        onSave={handleSave}
      />
    </div>
  );
};

export default ImagesSection;
